import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Mail, MessageSquare, Phone, Pencil, Smartphone, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useTiers } from '@/hooks/queries/useTiers';
import { useRelances, useRelanceTemplates } from '@/hooks/queries/useRelances';
import { formatDateTimeDZ } from '@/lib/format';
import { t } from '@/locales/i18n';
import type { RelanceCanal, RelanceNiveau, RelanceRow, RelanceStatut } from '@/lib/database.types';
import { TemplatesEditor } from './TemplatesEditor';

const NIVEAU_VARIANT: Record<RelanceNiveau, 'outline' | 'warning' | 'destructive' | 'success'> = {
  '1ere': 'outline',
  '2eme': 'warning',
  mise_en_demeure: 'destructive',
  contentieux: 'destructive',
  vip_soft: 'success',
};

const STATUT_VARIANT: Record<
  RelanceStatut,
  'default' | 'success' | 'warning' | 'destructive' | 'outline'
> = {
  planifiee: 'outline',
  envoyee: 'success',
  echec: 'destructive',
  lue: 'default',
};

const CANAL_ICON: Record<RelanceCanal, JSX.Element> = {
  email: <Mail className="h-3.5 w-3.5" />,
  whatsapp: <Smartphone className="h-3.5 w-3.5" />,
  sms: <MessageSquare className="h-3.5 w-3.5" />,
  telephone: <Phone className="h-3.5 w-3.5" />,
  courrier: <FileText className="h-3.5 w-3.5" />,
  mise_en_demeure_pdf: <FileText className="h-3.5 w-3.5" />,
};

export function RelancesPage(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const [niveau, setNiveau] = useState<RelanceNiveau | ''>('');
  const [canal, setCanal] = useState<RelanceCanal | ''>('');
  const [editTemplates, setEditTemplates] = useState(false);

  const { data: relances = [], isLoading } = useRelances({
    entrepriseId,
    niveau: niveau || null,
    canal: canal || null,
  });
  const { data: templates = [] } = useRelanceTemplates(entrepriseId);
  const { data: tiers = [] } = useTiers({ entrepriseId, search: '' });

  const tierMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of tiers) m.set(tt.id, tt.raison_sociale);
    return m;
  }, [tiers]);

  const columns = useMemo<ColumnDef<RelanceRow>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Date',
        size: 140,
        cell: ({ getValue }) => (
          <span className="num text-xs">{formatDateTimeDZ(String(getValue()))}</span>
        ),
      },
      {
        accessorKey: 'tier_id',
        header: 'Tiers',
        size: 220,
        cell: ({ getValue }) => (
          <span className="text-xs">{tierMap.get(getValue() as string) ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'niveau',
        header: 'Niveau',
        size: 130,
        cell: ({ getValue }) => {
          const n = getValue() as RelanceNiveau;
          return <Badge variant={NIVEAU_VARIANT[n]}>{t(`echeances.relances.niveau.${n}`)}</Badge>;
        },
      },
      {
        accessorKey: 'canal',
        header: 'Canal',
        size: 110,
        cell: ({ getValue }) => {
          const c = getValue() as RelanceCanal;
          return (
            <span className="inline-flex items-center gap-1 text-xs">
              {CANAL_ICON[c]}
              {t(`echeances.relances.canal.${c}`)}
            </span>
          );
        },
      },
      {
        accessorKey: 'sujet',
        header: 'Sujet / Aperçu',
        cell: ({ row }) => (
          <span className="line-clamp-1 text-xs">
            {row.original.sujet ?? row.original.contenu?.slice(0, 80) ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'destinataire',
        header: 'Destinataire',
        size: 200,
        cell: ({ getValue }) => (
          <span className="font-mono text-[11px]">{(getValue() as string) ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'statut',
        header: 'Statut',
        size: 110,
        cell: ({ getValue }) => {
          const s = getValue() as RelanceStatut;
          return <Badge variant={STATUT_VARIANT[s]}>{t(`echeances.relances.statut.${s}`)}</Badge>;
        },
      },
    ],
    [tierMap],
  );

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('echeances.relances.title')}
        subtitle={t('echeances.relances.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <Button variant="outline" onClick={() => setEditTemplates(true)}>
              <Pencil className="h-4 w-4" />
              {t('echeances.relances.editTemplates')}
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card p-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Niveau</label>
          <select
            value={niveau}
            onChange={(e) => setNiveau(e.target.value as RelanceNiveau | '')}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">— Tous —</option>
            {(
              ['1ere', '2eme', 'mise_en_demeure', 'contentieux', 'vip_soft'] as RelanceNiveau[]
            ).map((n) => (
              <option key={n} value={n}>
                {t(`echeances.relances.niveau.${n}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Canal</label>
          <select
            value={canal}
            onChange={(e) => setCanal(e.target.value as RelanceCanal | '')}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">— Tous —</option>
            {(['email', 'whatsapp', 'sms', 'telephone', 'courrier'] as RelanceCanal[]).map((c) => (
              <option key={c} value={c}>
                {t(`echeances.relances.canal.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto text-xs text-muted-foreground">
          {relances.length} relance{relances.length > 1 ? 's' : ''} ·{' '}
          <span className="font-medium">{templates.length}</span> template(s) configurés
        </div>
      </div>

      <DataTable
        data={relances}
        columns={columns}
        loading={isLoading}
        pageSize={50}
        searchPlaceholder="Rechercher…"
        exportFileName="relances.csv"
      />

      <TemplatesEditor
        open={editTemplates}
        onOpenChange={setEditTemplates}
        entrepriseId={entrepriseId}
        templates={templates}
      />
    </div>
  );
}
